import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Check, X, Loader2, ImageIcon, Inbox, CheckCircle2, XCircle, Maximize2, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";
import Btn from "../components/Btn";
import api, { API, apiErr } from "../lib/api";

const MIN_SCALE = 1, MAX_SCALE = 6;

function ZoomLightbox({ src, onClose }) {
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const drag = useRef(null);
  const pinch = useRef(null);
  const pointers = useRef(new Map());

  const clamp = (s) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));
  const reset = () => { setScale(1); setPos({ x: 0, y: 0 }); };
  const zoomBy = (d) => setScale((s) => { const n = clamp(s + d); if (n === 1) setPos({ x: 0, y: 0 }); return n; });

  const onWheel = (e) => { e.preventDefault(); zoomBy(e.deltaY < 0 ? 0.4 : -0.4); };

  const onPointerDown = (e) => {
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinch.current = { dist: Math.hypot(a.x - b.x, a.y - b.y), scale };
      drag.current = null;
    } else if (scale > 1) {
      drag.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    }
  };
  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pinch.current && pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      setScale(clamp(pinch.current.scale * (dist / pinch.current.dist)));
    } else if (drag.current) {
      setPos({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y });
    }
  };
  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    if (pointers.current.size === 0) { drag.current = null; if (scale === 1) setPos({ x: 0, y: 0 }); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/90 backdrop-blur-sm flex flex-col p-4" onClick={onClose} data-testid="proof-lightbox">
      <div className="flex justify-end gap-2 mb-2" onClick={(e) => e.stopPropagation()}>
        <button onClick={() => zoomBy(-0.5)} data-testid="proof-zoom-out" title="Perkecil"
          className="w-11 h-11 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"><ZoomOut className="w-5 h-5" /></button>
        <span className="grid place-items-center px-3 rounded-full bg-white/10 text-white font-heading font-black text-sm" data-testid="proof-zoom-level">{Math.round(scale * 100)}%</span>
        <button onClick={() => zoomBy(0.5)} data-testid="proof-zoom-in" title="Perbesar"
          className="w-11 h-11 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"><ZoomIn className="w-5 h-5" /></button>
        <button onClick={reset} data-testid="proof-zoom-reset" title="Reset zoom"
          className="w-11 h-11 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"><RotateCcw className="w-5 h-5" /></button>
        <button onClick={onClose} data-testid="proof-lightbox-close" title="Tutup"
          className="w-11 h-11 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"><X className="w-6 h-6" /></button>
      </div>
      <div className="flex-1 overflow-hidden grid place-items-center touch-none select-none"
        onClick={(e) => e.stopPropagation()} onWheel={onWheel}
        onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
        onDoubleClick={() => (scale > 1 ? reset() : zoomBy(1.5))}>
        <img src={src} alt="bukti penuh" draggable={false}
          style={{ transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`, cursor: scale > 1 ? "grab" : "zoom-in", transition: pinch.current || drag.current ? "none" : "transform 0.12s" }}
          className="max-w-full max-h-[calc(100vh-6rem)] object-contain rounded-xl" data-testid="proof-lightbox-img" />
      </div>
      <p className="text-center text-white/50 text-xs font-medium mt-2" onClick={(e) => e.stopPropagation()}>Scroll / cubit untuk zoom · seret untuk geser · dobel-klik untuk zoom cepat</p>
    </div>
  );
}

const REQ_LABELS = [
  { key: "reposted_story", label: "Repost ke Story", required: true },
  { key: "story_public", label: "Story Publik", required: true },
  { key: "followed_ig", label: "Follow Instagram", required: true },
  { key: "followed_tiktok", label: "Follow TikTok", required: true },
  { key: "followed_fb", label: "Follow Facebook (opsional)", required: false },
];

export default function AdminApprovals({ onCountChange }) {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [acting, setActing] = useState(null);
  const [rejecting, setRejecting] = useState(null); // submission being rejected
  const [comment, setComment] = useState("");
  const [preview, setPreview] = useState(null); // full-image lightbox src

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, tok] = await Promise.all([
        api.get("/admin/share-submissions?status=pending"),
        api.get("/auth/ws-token"),
      ]);
      setSubs(data.submissions);
      setToken(tok.data.token);
      onCountChange?.(data.submissions.length);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    setActing(id);
    try {
      await api.post(`/admin/share-submissions/${id}/review?approve=true`);
      toast.success("Disetujui! Tiket & bonus token diberikan.");
      setSubs((s) => { const n = s.filter((x) => x.id !== id); onCountChange?.(n.length); return n; });
    } catch (e) { toast.error(apiErr(e)); } finally { setActing(null); }
  };

  const doReject = async () => {
    if (!comment.trim()) { toast.error("Tulis alasan penolakan agar siswa tahu kesalahannya."); return; }
    const id = rejecting.id;
    setActing(id);
    try {
      await api.post(`/admin/share-submissions/${id}/review?approve=false&comment=${encodeURIComponent(comment.trim())}`);
      toast.success("Ditolak dengan catatan untuk siswa.");
      setSubs((s) => { const n = s.filter((x) => x.id !== id); onCountChange?.(n.length); return n; });
      setRejecting(null); setComment("");
    } catch (e) { toast.error(apiErr(e)); } finally { setActing(null); }
  };

  if (loading) return <div className="grid place-items-center py-16"><Loader2 className="w-10 h-10 animate-spin text-su-primary" /></div>;

  return (
    <div className="card-chunky p-6">
      {preview && <ZoomLightbox src={preview} onClose={() => setPreview(null)} />}
      {rejecting && (
        <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm grid place-items-center p-4" onClick={() => { setRejecting(null); setComment(""); }} data-testid="reject-modal">
          <div className="card-chunky p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading font-black text-lg mb-1">Tolak Bukti — Beri Catatan</h3>
            <p className="text-su-muted font-medium text-sm mb-4">Jelaskan kenapa ditolak, mis. "Story-mu masih Close Friends" atau "Belum follow TikTok". Catatan ini akan dilihat siswa.</p>
            <textarea data-testid="reject-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={3} autoFocus
              placeholder="Contoh: Screenshot tidak menunjukkan repost di Story, atau Story belum diatur Publik."
              className="w-full bg-su-bg border-2 border-su-border rounded-xl px-4 py-2.5 font-medium focus:border-su-danger outline-none transition-colors" />
            <div className="flex gap-3 mt-4">
              <Btn variant="danger" data-testid="reject-confirm" onClick={doReject} disabled={acting === rejecting.id} className="flex-1"><X className="w-4 h-4" /> Tolak & Kirim Catatan</Btn>
              <Btn variant="ghost" onClick={() => { setRejecting(null); setComment(""); }}>Batal</Btn>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 mb-1"><ImageIcon className="w-5 h-5 text-su-primary" /><h2 className="font-heading font-black text-xl">Verifikasi Bukti Share</h2></div>
      <p className="text-su-muted font-medium text-sm mb-5">Setujui screenshot untuk memberi tiket Tryout Akbar (+3 token bonus). Jika menolak, beri catatan agar siswa tahu salahnya.</p>

      {subs.length === 0 ? (
        <div className="text-center py-12">
          <Inbox className="w-14 h-14 text-su-muted mx-auto mb-3" />
          <p className="font-heading font-extrabold text-su-muted">Tidak ada bukti menunggu verifikasi 🎉</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {subs.map((s) => (
            <div key={s.id} className="rounded-2xl border-2 border-su-border overflow-hidden" data-testid={`submission-${s.id}`}>
              {(() => {
                const imgs = s.storage_paths && s.storage_paths.length ? s.storage_paths : [s.storage_path];
                const cols = imgs.length > 1 ? "grid-cols-2" : "grid-cols-1";
                return (
                  <div className={`grid ${cols} gap-1 bg-su-track p-1`} data-testid={`proof-grid-${s.id}`}>
                    {imgs.map((p, i) => (
                      <button key={i} type="button" onClick={() => setPreview(`${API}/files/${p}?auth=${token}`)}
                        className="block aspect-video bg-su-track group relative cursor-zoom-in overflow-hidden rounded-md"
                        data-testid={`view-proof-${s.id}-${i}`} title="Klik untuk lihat bukti penuh">
                        <img src={`${API}/files/${p}?auth=${token}`} alt={`bukti ${i + 1}`} className="w-full h-full object-cover" />
                        {imgs.length > 1 && <span className="absolute top-1 left-1 text-[10px] font-heading font-black bg-black/60 text-white px-1.5 py-0.5 rounded">{i + 1}/{imgs.length}</span>}
                        <span className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors grid place-items-center opacity-0 group-hover:opacity-100">
                          <span className="inline-flex items-center gap-1 text-white font-heading font-black text-[11px] bg-black/60 px-2 py-1 rounded-full"><Maximize2 className="w-3.5 h-3.5" /> Lihat Penuh</span>
                        </span>
                      </button>
                    ))}
                  </div>
                );
              })()}
              <div className="p-3">
                <p className="font-heading font-extrabold text-sm truncate">{s.user_name || "User"}</p>
                <p className="text-xs text-su-muted font-bold mb-2">{new Date(s.created_at).toLocaleString("id-ID")}</p>
                <div className="flex flex-wrap gap-1 mb-3">
                  {REQ_LABELS.map((r) => (
                    <span key={r.key} className={`inline-flex items-center gap-1 text-[10px] font-heading font-black px-1.5 py-0.5 rounded-md ${s[r.key] ? "bg-su-success/15 text-su-success" : (r.required ? "bg-su-danger/15 text-su-danger" : "bg-su-track text-su-muted")}`}>
                      {s[r.key] ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />} {r.label}
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Btn variant="success" size="sm" className="flex-1" data-testid={`approve-${s.id}`} disabled={acting === s.id} onClick={() => approve(s.id)}>
                    <Check className="w-4 h-4" /> Setujui
                  </Btn>
                  <Btn variant="danger" size="sm" data-testid={`reject-${s.id}`} disabled={acting === s.id} onClick={() => { setRejecting(s); setComment(""); }}>
                    <X className="w-4 h-4" /> Tolak
                  </Btn>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
